import { Box, Stack } from '@mui/material';
import { BigChartCard } from 'charts/common/BigChartCard';
import { ChartCardHeading } from 'charts/common/ChartCardHeading';
import { getChartDefinition } from 'charts/catalog/chartCatalog';
import { CatalogChartRenderer } from 'charts/components/CatalogChartRenderer';
import { CHART_EXPORT_IGNORE_ATTRIBUTE } from 'charts/export';
import { resolveChartCardSubtitle } from 'charts/formatters/resolveChartCardSubtitle';
import { getChartAreaSx } from 'charts/theme/muiChartSlotProps';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import React, { ReactElement, useRef } from 'react';
import { useIntl } from 'react-intl';
import { MemoChartReloader } from '../chartReloader';
import { ChartExportMenu } from 'charts/export/ChartExportMenu';
import { ChartRefreshButton } from './ChartDetailHeader';
import { formatChartLastUpdate } from './helpers';
import { chartDetailsRootSx } from './styles';
import type { ChartDetailsPresentationalProps } from './types';

function ChartDetailsPresentational({
  chartId,
  context,
  isOffline,
  updatedAt,
  onRefresh,
  filtersRenderer,
}: ChartDetailsPresentationalProps): ReactElement {
  const intl = useIntl();
  const definition = getChartDefinition(chartId);
  const title = formatTranslation(intl, unsafeTranslationKey(context.titleId));
  const subtitle = resolveChartCardSubtitle(definition, intl);
  const lastUpdate = formatChartLastUpdate(intl, updatedAt);
  const snapshotRef = useRef<HTMLDivElement>(null);

  return (
    <MemoChartReloader context={context}>
      <Box sx={chartDetailsRootSx}>
        <BigChartCard>
          <Box ref={snapshotRef}>
            <ChartCardHeading
              chartId={chartId}
              title={title}
              subtitle={subtitle}
              titleComponent='h1'
              lastUpdate={lastUpdate}
              actions={
                <Stack
                  direction='row'
                  gap={1}
                  {...{ [CHART_EXPORT_IGNORE_ATTRIBUTE]: true }}
                >
                  <ChartExportMenu
                    chartId={chartId}
                    title={title}
                    snapshotRef={snapshotRef}
                  />
                  <ChartRefreshButton
                    chartId={chartId}
                    isOffline={isOffline}
                    isRefreshing={context.loading}
                    onRefresh={onRefresh}
                  />
                </Stack>
              }
            />
            <Box
              sx={{
                ...getChartAreaSx(),
                width: '100%',
                minHeight: 400,
                mt: 2,
              }}
            >
              <CatalogChartRenderer chartId={chartId} height={400} />
            </Box>
          </Box>
        </BigChartCard>

        <BigChartCard noPadding>{filtersRenderer}</BigChartCard>
      </Box>
    </MemoChartReloader>
  );
}

export const MemoChartDetailsPresentational = React.memo(
  ChartDetailsPresentational
);
